import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    ClientInactivityAlertNoticeResource,
    clearClientInactivityAlertNotice,
    getClientInactivityAlertActiveNotices,
} from '@data/api/clientInactivityAlert';

export interface UseClientInactivityAlertNoticesResult {
    // Server-authoritative "Show in dashboard". While false the map is always empty, so no
    // warning can be drawn from a stale client-side copy of the toggle
    isDashboardEnabled: boolean;
    noticeByClientId: Record<string, ClientInactivityAlertNoticeResource>;
    isLoading: boolean;
    clearNotice: (clientUserId: string) => Promise<void>;
    clearingClientUserId: string | null;
}

// Fetches the standing inactivity notices for a bounded set of clients.
//
// Deliberately quiet on failure: these warnings are supplementary, and a client list that
// refuses to render because one side lookup failed would be a worse page than one without
// icons. The list simply stays empty.
const useClientInactivityAlertNotices = (clientUserIds: string[]): UseClientInactivityAlertNoticesResult => {
    // A stable key, so the effect runs when the set of clients really changes and not on every
    // parent render that hands over a fresh array
    const key = useMemo(() => Array.from(new Set(clientUserIds)).sort().join(','), [clientUserIds]);

    const [isDashboardEnabled, setIsDashboardEnabled] = useState(false);
    const [noticeByClientId, setNoticeByClientId] = useState<Record<string, ClientInactivityAlertNoticeResource>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [clearingClientUserId, setClearingClientUserId] = useState<string | null>(null);

    // Bumped only by a clear that the server declined to apply, so the next render re-reads the
    // real notices exactly once. Nothing in the effect itself touches it, so there is no loop
    const [refreshToken, setRefreshToken] = useState(0);

    useEffect(() => {
        const ids = key.length === 0 ? [] : key.split(',');

        if (ids.length === 0) {
            setIsDashboardEnabled(false);
            setNoticeByClientId({});
            return;
        }

        let isMounted = true;
        setIsLoading(true);

        getClientInactivityAlertActiveNotices(ids)
            .then((result) => {
                if (!isMounted) {
                    return;
                }

                const map: Record<string, ClientInactivityAlertNoticeResource> = {};
                result.items.forEach((notice) => {
                    map[notice.client_user_id] = notice;
                });

                setIsDashboardEnabled(result.is_dashboard_enabled);
                setNoticeByClientId(map);
            })
            .catch(() => {
                if (isMounted) {
                    setIsDashboardEnabled(false);
                    setNoticeByClientId({});
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [key, refreshToken]);

    const clearNotice = useCallback(async (clientUserId: string) => {
        setClearingClientUserId(clientUserId);

        try {
            const result = await clearClientInactivityAlertNotice(clientUserId);

            // Clearing is server-global, and a 2xx is not by itself a dismissal: the endpoint
            // answers `is_cleared: false` when there was nothing standing to clear. Hiding the
            // warning on that answer would invent a state the next lookup contradicts, so the
            // server is re-read and the caller's existing failure toast is left to report it
            if (result?.is_cleared !== true) {
                setRefreshToken((previous) => previous + 1);
                throw new Error('Inactivity notice was not cleared');
            }

            // The server kept the row and only recorded the dismissal; the warning is what goes
            setNoticeByClientId((previous) => {
                const next = { ...previous };
                delete next[clientUserId];
                return next;
            });
        } finally {
            setClearingClientUserId(null);
        }
    }, []);

    return { isDashboardEnabled, noticeByClientId, isLoading, clearNotice, clearingClientUserId };
};

export default useClientInactivityAlertNotices;
